import { useEffect, useState } from 'react';
import { AlertCircle, KeyRound } from 'lucide-react';
import type { AxiosError } from 'axios';
import type { Credential } from '../hooks/useCredentials';
import { useReplaceCredentialMaterial } from '../hooks/useCredentials';
import { warnUndelivered } from './credentialDelivery';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

type CredType = Credential['type'];

/**
 * Republish a credential's material into the credential that already exists.
 *
 * Two situations, one mechanism. When every gateway holding a secret is gone —
 * a sole gateway lost, most often — the secret is gone with it, while the
 * credential and every device pointing at it remain: nothing can recover it
 * except an operator typing it again. And when material is simply being
 * replaced, the same call rotates it. The dialog only changes what it *says*,
 * because what the operator needs to know differs: one is a repair, the other
 * is a change with live consequences.
 *
 * The type is fixed and shown, not chosen. Changing it under a live id would
 * reinterpret every device that references the credential, all at once.
 */
export function CredentialMaterialDialog({
  credential,
  organizationId,
  mode,
  deviceCount,
  onClose,
}: {
  credential: Credential;
  organizationId: string | null | undefined;
  /** `restore` when nothing holds the material any more, `rotate` otherwise. */
  mode: 'restore' | 'rotate';
  /** How many devices reference this credential, for the warning. */
  deviceCount?: number;
  onClose: (replaced: boolean) => void;
}) {
  const type: CredType = credential.type;
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [sshKey, setSshKey] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [preSharedKey, setPreSharedKey] = useState('');
  const [community, setCommunity] = useState('');
  const [v3AuthKey, setV3AuthKey] = useState('');
  const [v3PrivKey, setV3PrivKey] = useState('');
  const [error, setError] = useState<string | null>(null);

  const replace = useReplaceCredentialMaterial(organizationId);

  // Never let secrets outlive the dialog that collected them.
  useEffect(() => {
    return () => {
      setPassword('');
      setSshKey('');
      setApiKey('');
      setPreSharedKey('');
      setCommunity('');
      setV3AuthKey('');
      setV3PrivKey('');
    };
  }, []);

  const submit = () => {
    setError(null);
    let body: Parameters<typeof replace.mutate>[0];
    switch (type) {
      case 'usernamePassword':
        if (!username || !password) {
          setError('Username and password are required.');
          return;
        }
        body = { id: credential.id, type, username, password };
        break;
      case 'sshKey':
        if (!sshKey) {
          setError('SSH key content is required.');
          return;
        }
        body = { id: credential.id, type, key: sshKey };
        break;
      case 'apiKey':
        if (!apiKey) {
          setError('API key is required.');
          return;
        }
        body = { id: credential.id, type, key: apiKey };
        break;
      case 'preSharedKey':
        if (!preSharedKey) {
          setError('The pre-shared key is required.');
          return;
        }
        body = { id: credential.id, type, key: preSharedKey };
        break;
      case 'snmpV2Community':
        if (!community) {
          setError('Community string is required.');
          return;
        }
        body = { id: credential.id, type, community };
        break;
      case 'snmpV3':
        if (!username) {
          setError('Username is required for SNMPv3.');
          return;
        }
        body = {
          id: credential.id,
          type,
          username,
          authKey: v3AuthKey || undefined,
          privKey: v3PrivKey || undefined,
        };
        break;
      default:
        setError(`Material for ${type} credentials cannot be replaced here yet.`);
        return;
    }

    replace.mutate(body, {
      onSuccess: (result) => {
        onClose(true);
        warnUndelivered('New secret saved', result.undelivered);
      },
      onError: (err) => {
        // The backend refuses rather than half-succeeding, and its reason is
        // the useful part: no online gateway, a type mismatch, or a credential
        // the controller owns. Surfacing our own wording would hide all three.
        const detail = (err as AxiosError<{ description?: string }>).response?.data.description;
        setError(detail ?? 'The material could not be delivered. The credential is unchanged.');
      },
    });
  };

  const restoring = mode === 'restore';

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(false); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="w-4 h-4" />
            {restoring ? 'Restore credential material' : 'Replace credential material'}
          </DialogTitle>
          <DialogDescription>
            {restoring
              ? `No gateway holds the material for "${credential.name}" any more, so it cannot be recovered — enter it again to put it back. The credential itself, and everything using it, is unchanged.`
              : `Replaces the material behind "${credential.name}". Everything using this credential keeps using it; only the secret changes.`}
          </DialogDescription>
        </DialogHeader>

        {!restoring ? (
          <Alert>
            <AlertCircle className="w-4 h-4" />
            <AlertDescription>
              This changes what we send to devices, not what the devices accept.
              Change it on the device first, or polling will start failing
              {typeof deviceCount === 'number' && deviceCount > 0
                ? ` for the ${String(deviceCount)} device${deviceCount === 1 ? '' : 's'} using this credential.`
                : '.'}
            </AlertDescription>
          </Alert>
        ) : null}

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Type</Label>
            {/* Fixed, not chosen: see the component doc. */}
            <p className="text-sm text-muted-foreground">{type}</p>
          </div>

          {type === 'usernamePassword' || type === 'snmpV3' ? (
            <div className="space-y-1.5">
              <Label htmlFor="cmd-user">Username</Label>
              <Input
                id="cmd-user"
                autoComplete="off"
                value={username}
                onChange={(e) => { setUsername(e.target.value); }}
              />
            </div>
          ) : null}

          {type === 'usernamePassword' ? (
            <div className="space-y-1.5">
              <Label htmlFor="cmd-pass">Password</Label>
              <Input
                id="cmd-pass"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); }}
              />
            </div>
          ) : null}

          {type === 'sshKey' ? (
            <div className="space-y-1.5">
              <Label htmlFor="cmd-key">Private key</Label>
              <textarea
                id="cmd-key"
                className="w-full min-h-32 rounded-md border bg-background p-2 font-mono text-xs"
                value={sshKey}
                onChange={(e) => { setSshKey(e.target.value); }}
              />
            </div>
          ) : null}

          {type === 'apiKey' ? (
            <div className="space-y-1.5">
              <Label htmlFor="cmd-apikey">API key</Label>
              <Input
                id="cmd-apikey"
                type="password"
                autoComplete="off"
                value={apiKey}
                onChange={(e) => { setApiKey(e.target.value); }}
              />
            </div>
          ) : null}

          {type === 'preSharedKey' ? (
            <div className="space-y-1.5">
              <Label htmlFor="cmd-psk">Pre-shared key</Label>
              <Input
                id="cmd-psk"
                type="password"
                autoComplete="off"
                value={preSharedKey}
                onChange={(e) => { setPreSharedKey(e.target.value); }}
              />
            </div>
          ) : null}

          {type === 'snmpV2Community' ? (
            <div className="space-y-1.5">
              <Label htmlFor="cmd-community">Community string</Label>
              <Input
                id="cmd-community"
                type="password"
                autoComplete="off"
                value={community}
                onChange={(e) => { setCommunity(e.target.value); }}
              />
            </div>
          ) : null}

          {type === 'snmpV3' ? (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="cmd-authkey">Auth key</Label>
                <Input
                  id="cmd-authkey"
                  type="password"
                  autoComplete="off"
                  value={v3AuthKey}
                  onChange={(e) => { setV3AuthKey(e.target.value); }}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="cmd-privkey">Privacy key</Label>
                <Input
                  id="cmd-privkey"
                  type="password"
                  autoComplete="off"
                  value={v3PrivKey}
                  onChange={(e) => { setV3PrivKey(e.target.value); }}
                />
              </div>
            </>
          ) : null}
        </div>

        {error ? (
          <Alert variant="destructive">
            <AlertCircle className="w-4 h-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}

        <DialogFooter>
          <Button variant="outline" onClick={() => { onClose(false); }} disabled={replace.isPending}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={replace.isPending}>
            {replace.isPending ? 'Sending…' : restoring ? 'Restore' : 'Replace'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
